<script setup lang="ts">
import SensorResource from '@/api/resources/sensor'
import FilledButton from '@/components/Globals/Buttons/FilledButton.vue'
import CardInfo from '@/components/Globals/CardInfo.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import ShimmerRealtimeGroup from '@/components/Globals/Feedbacks/ShimmerRealtimeGroup.vue'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import AlarmsConfig from '@/components/Sensors/AlarmsConfig.vue'
import DoorOpenChart from '@/components/Sensors/DoorOpenChart.vue'
import EnergyCurrentChart from '@/components/Sensors/EnergyCurrentChart.vue'
import EnergyVoltageChart from '@/components/Sensors/EnergyVoltageChart.vue'
import RealtimeChart from '@/components/Sensors/RealtimeChart.vue'
import RealtimeGroupCard from '@/components/Sensors/RealtimeGroupCard.vue'
import LastCommunicationCard from '@/components/Sensors/LastCommunicationCard.vue'
import TemperatureHybridChart from '@/components/Sensors/TemperatureHybridChart.vue'
import TemperatureStatusCard from '@/components/Sensors/TemperatureStatusCard.vue'
import { ISchemaSensor } from '@/interfaces/entities/schema_sensor'
import { getSensorColor, SensorType, translateSensorType, UserRoles } from '@/interfaces/enums'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useSensorStore } from '@/store/sensor'
import { useUserStore } from '@/store/user'
import { useCompanyStore } from '@/store/company'
import EditSensorModal from '@/views/Sensors/modals/EditSensorModal.vue'
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import { InformationCircleIcon } from '@heroicons/vue/24/outline'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { IAlarmTypeConfig } from '@/interfaces/entities/company'
import { buildHoursRange, formatApiTimeLocal } from '@/utils/date/date-time'
import {
  buildHistoricalDataPointsFromEnergyReadings,
  collectAvailableConsoleMetricKeys,
  extractDynamicReadingKeys,
  filterKeysByMetricGroup,
  filterSeriesByAvailableKeys,
  getLastHistoricalTimestampMs,
  isCurrentFieldKey,
  isFixedValueKey,
  isPowerFactorFieldKey,
  isVoltageFieldKey,
  mergeHistoricalDataPoints,
  normalizeFixedValues,
  normalizeInstantReadingsForHistory,
  pickRecordEntriesByKeys,
  shouldShowEnergyConsoleMetricGroup,
  splitFixedValuesByGroup,
  type IRealtimeHistoricalDataPoint,
} from '@/utils/sensor-realtime-history'

const route = useRoute()
const router = useRouter()
const store = useSensorStore()
const userStore = useUserStore()
const companyStore = useCompanyStore()
const { fetchCompanyAlarmTypes } = companyStore
const { getAlarmTypeConfigs } = storeToRefs(companyStore)
const { t } = useI18n()

const isAlarmTypeAvailable = (alarmTypeKey: string): boolean => {
  return getAlarmTypeConfigs.value.some((config: IAlarmTypeConfig) => config.name === alarmTypeKey)
}

const sensorId = computed(() => Number(route.params.id))
const isLoading = ref(true)
const sensorDetails = ref<any>(null)
const isEditModalOpen = ref(false)

// Schema management
const schemaOptions = ref<Array<ISchemaSensor>>([])
const selectedSchema = ref<ISchemaSensor | null>(null)

const previewSchema = computed(() => {
  if (selectedSchema.value) {
    return selectedSchema.value.schema || selectedSchema.value
  }
  if (sensorSchema.value) {
    return sensorSchema.value.schema || sensorSchema.value
  }
  return null
})

// Flag para evitar atualizações durante desmontagem
const isComponentMounted = ref(true)

// Refs locais atualizados via polling
const readings = ref<Record<string, any>>({})
const isPolling = ref(false)
const pollingError = ref<string | null>(null)
const lastDataSentAt = ref<number | null>(null)
const pollingIntervalSeconds = ref(5)
const pollingIntervalOptions = [5, 10, 15, 30, 60]
let pollingTimer: ReturnType<typeof setInterval> | null = null

// Histórico de dados para mini-gráficos RealtimeChart (seed 1h + pontos do polling)
const REALTIME_MINI_CHART_HISTORY_HOURS = 1
const REALTIME_MINI_CHART_WINDOW_PERIOD = '1m'
const MAX_HISTORY_POINTS = 72

const historicalData = ref<IRealtimeHistoricalDataPoint[]>([])
const fixedValues = ref<Record<string, number>>({})
const dynamicCurrentKeys = ref<string[]>([])
const dynamicVoltageKeys = ref<string[]>([])
const dynamicPowerFactorKeys = ref<string[]>([])
/** Seed dos mini-gráficos concluída — decide quais grupos Corrente/Tensão/FP exibir. */
const hasMiniChartSeedCompleted = ref(false)
const nowTimestamp = ref(Date.now())
let relativeTimeTimer: ReturnType<typeof setInterval> | null = null
const lastHistoryTimestampMs = ref<number | null>(null)

const formattedLastDataSentAt = computed(() => {
  if (!lastDataSentAt.value) return '-'
  return new Date(lastDataSentAt.value).toLocaleString('pt-BR')
})

const lastDataAgeLabel = computed(() => {
  if (!lastDataSentAt.value) return t('sensor.detail.age.no_recent_reading')

  const diffMs = Math.max(0, nowTimestamp.value - lastDataSentAt.value)
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return t('sensor.detail.age.less_than_one_minute')

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t('sensor.detail.age.minutes_ago', { count: minutes })

  const hours = Math.floor(minutes / 60)
  const restMinutes = minutes % 60
  if (hours < 24) {
    return restMinutes > 0
      ? t('sensor.detail.age.hours_minutes_ago', { hours, minutes: restMinutes })
      : t('sensor.detail.age.hours_ago', { hours })
  }

  const days = Math.floor(hours / 24)
  const restHours = hours % 24
  return restHours > 0
    ? t('sensor.detail.age.days_hours_ago', { days, hours: restHours })
    : t('sensor.detail.age.days_ago', { days })
})

function appendHistory(readingsForHistory: Record<string, unknown>, timestampIso?: string | null) {
  if (!timestampIso) return

  const timestampMs = new Date(timestampIso).getTime()
  if (Number.isNaN(timestampMs)) return

  // Evita pontos repetidos quando o polling retorna a mesma leitura.
  if (lastHistoryTimestampMs.value !== null && timestampMs <= lastHistoryTimestampMs.value) return

  const schema = sensorDetails.value?.schema_sensor?.schema as Record<string, string> | undefined
  const normalizedReadings = normalizeInstantReadingsForHistory(readingsForHistory, schema)

  const historyPoint: IRealtimeHistoricalDataPoint = {
    time: formatApiTimeLocal(timestampIso),
    timestampMs,
    ...normalizedReadings,
  }

  historicalData.value.push(historyPoint)
  lastHistoryTimestampMs.value = timestampMs
  if (historicalData.value.length > MAX_HISTORY_POINTS) {
    historicalData.value.shift()
  }
}

async function seedMiniChartHistoryFromReadings() {
  const id = sensorId.value
  if (!id || !sensorDetails.value || sensorDetails.value.sensor_type !== 'electricity') return

  try {
    const { start, end } = buildHoursRange(REALTIME_MINI_CHART_HISTORY_HOURS)
    const dateRange = `start_date=${encodeURIComponent(start.toISOString())}&end_date=${encodeURIComponent(end.toISOString())}`
    const schema = sensorDetails.value?.schema_sensor?.schema as Record<string, string> | undefined

    const [currentResult, voltagePowerFactorResult] = await Promise.allSettled([
      SensorResource.getEnergySensorReadings({
        sensorId: id,
        dateRange,
        resolution: REALTIME_MINI_CHART_WINDOW_PERIOD,
        aggregation: 'mean',
      }),
      SensorResource.getEnergyVoltagePowerFactorReadings({
        sensorId: id,
        dateRange,
        resolution: REALTIME_MINI_CHART_WINDOW_PERIOD,
        aggregation: 'mean',
      }),
    ])

    let currentPoints: IRealtimeHistoricalDataPoint[] = []
    if (currentResult.status === 'fulfilled') {
      const raw = currentResult.value?.readings
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        currentPoints = buildHistoricalDataPointsFromEnergyReadings(
          raw as Record<string, Array<{ t?: string; v?: number | string; time?: string; value?: number | string }>>,
          schema,
        )
        dynamicCurrentKeys.value = extractDynamicReadingKeys(raw).filter(isCurrentFieldKey)
      } else {
        dynamicCurrentKeys.value = []
      }
    } else {
      dynamicCurrentKeys.value = []
    }

    let voltagePowerFactorPoints: IRealtimeHistoricalDataPoint[] = []
    if (voltagePowerFactorResult.status === 'fulfilled') {
      const response = voltagePowerFactorResult.value
      const raw = response?.readings
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        voltagePowerFactorPoints = buildHistoricalDataPointsFromEnergyReadings(
          raw as Record<string, Array<{ t?: string; v?: number | string; time?: string; value?: number | string }>>,
          schema,
        )
        const dynamicKeys = extractDynamicReadingKeys(raw)
        dynamicVoltageKeys.value = dynamicKeys.filter(isVoltageFieldKey)
        dynamicPowerFactorKeys.value = dynamicKeys.filter(isPowerFactorFieldKey)
      } else {
        dynamicVoltageKeys.value = []
        dynamicPowerFactorKeys.value = []
      }
      fixedValues.value = normalizeFixedValues(response?.fixed_values)
    } else {
      dynamicVoltageKeys.value = []
      dynamicPowerFactorKeys.value = []
      fixedValues.value = {}
    }
    hasMiniChartSeedCompleted.value = true

    const seededPoints = mergeHistoricalDataPoints(currentPoints, voltagePowerFactorPoints)
    if (!seededPoints.length) return

    historicalData.value = seededPoints
    lastHistoryTimestampMs.value = getLastHistoricalTimestampMs(seededPoints)
  } catch {
    hasMiniChartSeedCompleted.value = true
    // Falha silenciosa: polling continua preenchendo o histórico em tempo real.
  }
}

function getLastPointFromGroupedReadings(readings: unknown): { t?: string; v?: number; s?: string } | null {
  if (!readings || typeof readings !== 'object') return null

  const groups = Object.values(readings as Record<string, unknown>)
  for (const group of groups) {
    if (!Array.isArray(group) || group.length === 0) continue
    const last = group[group.length - 1]
    if (last && typeof last === 'object') {
      const typed = last as { t?: string; v?: number; s?: string }
      if (typed.t) return typed
    }
  }
  return null
}

async function pollLatestReadings() {
  const id = sensorId.value
  if (!id || !sensorDetails.value || !isComponentMounted.value) return

  try {
    if (sensorDetails.value.sensor_type === 'electricity') {
      const metrics = await SensorResource.getInfluxEnergyMetrics(id)
      const nextReadings = metrics?.readings ?? {}
      readings.value = { ...nextReadings }

      if (metrics?.last_reading_at) {
        const sentAt = new Date(metrics.last_reading_at).getTime()
        if (!Number.isNaN(sentAt)) lastDataSentAt.value = sentAt
      }

      appendHistory(nextReadings, metrics?.last_reading_at)
      pollingError.value = null
      return
    }

    if (sensorDetails.value.sensor_type === 'temperature') {
      const result = await SensorResource.getTemperatureInfluxLastReading(id)
      const lastPoint = getLastPointFromGroupedReadings(result?.readings)
      if (lastPoint?.v !== undefined) {
        readings.value = { temperature: lastPoint.v }
      }
      const ts = result?.last_reading_at ?? lastPoint?.t
      if (ts) {
        const sentAt = new Date(ts).getTime()
        if (!Number.isNaN(sentAt)) {
          lastDataSentAt.value = sentAt
        }
      }
      appendHistory(readings.value, ts)
      pollingError.value = null
      return
    }

    if (sensorDetails.value.sensor_type === 'door_open') {
      const result = await SensorResource.getOpenInfluxLastReading(id)
      const lastPoint = getLastPointFromGroupedReadings(result?.readings)
      if (lastPoint?.v !== undefined) {
        const firstKey = result?.readings ? Object.keys(result.readings)[0] : null
        if (firstKey) {
          readings.value = { 
            [firstKey]: lastPoint.v,
            door_status: lastPoint.s 
          }
        }
      }
      const ts = result?.last_reading_at ?? lastPoint?.t
      if (ts) {
        const sentAt = new Date(ts).getTime()
        if (!Number.isNaN(sentAt)) lastDataSentAt.value = sentAt
      }
      appendHistory(readings.value, ts)
      pollingError.value = null
    }
  } catch {
    pollingError.value = t('sensor.console.polling_error')
  }
}

function stopPolling() {
  isPolling.value = false
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
}

function startPolling() {
  stopPolling()
  isPolling.value = true
  pollLatestReadings()
  pollingTimer = setInterval(() => {
    pollLatestReadings()
  }, pollingIntervalSeconds.value * 1000)
}

// Agrupa dados por categoria
const fixedValuesByGroup = computed(() => splitFixedValuesByGroup(fixedValues.value))

const groupedData = computed(() => {
  const data = realtimeData.value
  const fixed = fixedValues.value
  return {
    corrente: {
      current_a: data.current_a,
      current_b: data.current_b,
      current_c: data.current_c,
    },
    tensao: {
      voltage_a: isFixedValueKey('voltage_a', fixed) ? fixed.voltage_a : data.voltage_a,
      voltage_b: isFixedValueKey('voltage_b', fixed) ? fixed.voltage_b : data.voltage_b,
      voltage_c: isFixedValueKey('voltage_c', fixed) ? fixed.voltage_c : data.voltage_c,
    },
    fatorPotencia: {
      power_factor: isFixedValueKey('power_factor', fixed) ? fixed.power_factor : data.power_factor,
      power_factor_a: isFixedValueKey('power_factor_a', fixed) ? fixed.power_factor_a : data.power_factor_a,
      power_factor_b: isFixedValueKey('power_factor_b', fixed) ? fixed.power_factor_b : data.power_factor_b,
      power_factor_c: isFixedValueKey('power_factor_c', fixed) ? fixed.power_factor_c : data.power_factor_c,
    },
  }
})

const hasFixedVoltageValues = computed(() => Object.keys(fixedValuesByGroup.value.voltage).length > 0)
const hasFixedPowerFactorValues = computed(() => Object.keys(fixedValuesByGroup.value.powerFactor).length > 0)
const hasAnyFixedConsoleValues = computed(
  () => hasFixedVoltageValues.value || hasFixedPowerFactorValues.value,
)

/** Chaves do console: readings dinâmicos ∪ fixed_values (corrente / tensão / FP). */
const availableConsoleMetricKeys = computed(() =>
  collectAvailableConsoleMetricKeys(
    [...dynamicCurrentKeys.value, ...dynamicVoltageKeys.value, ...dynamicPowerFactorKeys.value],
    fixedValues.value,
  ),
)

const availableCurrentKeys = computed(() => filterKeysByMetricGroup(availableConsoleMetricKeys.value, 'current'))
const availableVoltageKeys = computed(() => filterKeysByMetricGroup(availableConsoleMetricKeys.value, 'voltage'))
const availablePowerFactorKeys = computed(() =>
  filterKeysByMetricGroup(availableConsoleMetricKeys.value, 'powerFactor'),
)

/**
 * Grupo visível se houver ao menos uma chave em readings ou fixed_values.
 * Antes da seed, oculta para não flashar grupos inexistentes no schema.
 */
const showCurrentGroup = computed(() => {
  if (!hasMiniChartSeedCompleted.value) return false
  return shouldShowEnergyConsoleMetricGroup('current', dynamicCurrentKeys.value, fixedValues.value)
})

const showVoltageGroup = computed(() => {
  if (!hasMiniChartSeedCompleted.value) return false
  return shouldShowEnergyConsoleMetricGroup('voltage', dynamicVoltageKeys.value, fixedValues.value)
})

const showPowerFactorGroup = computed(() => {
  if (!hasMiniChartSeedCompleted.value) return false
  return shouldShowEnergyConsoleMetricGroup('powerFactor', dynamicPowerFactorKeys.value, fixedValues.value)
})

const currentCardEntries = computed(() =>
  pickRecordEntriesByKeys(groupedData.value.corrente, availableCurrentKeys.value),
)

const voltageCardEntries = computed(() =>
  pickRecordEntriesByKeys(groupedData.value.tensao, availableVoltageKeys.value),
)

const powerFactorCardEntries = computed(() =>
  pickRecordEntriesByKeys(groupedData.value.fatorPotencia, availablePowerFactorKeys.value),
)

// Dados históricos formatados para gráficos
const historicalCurrentData = computed(() => {
  if (!historicalData.value.length || !dynamicCurrentKeys.value.length) return []
  return historicalData.value.map((point) => {
    const row: Record<string, string | number | null | undefined> = { time: point.time }
    for (const key of dynamicCurrentKeys.value) {
      row[key] = point[key] ?? null
    }
    return row
  })
})

const historicalVoltageData = computed(() => {
  if (!historicalData.value.length || !dynamicVoltageKeys.value.length) return []
  return historicalData.value.map(point => {
    const row: Record<string, string | number | null | undefined> = { time: point.time }
    for (const key of dynamicVoltageKeys.value) {
      row[key] = point[key] ?? null
    }
    return row
  })
})

const historicalPowerFactorData = computed(() => {
  if (!historicalData.value.length || !dynamicPowerFactorKeys.value.length) return []
  return historicalData.value.map(point => {
    const row: Record<string, string | number | null | undefined> = { time: point.time }
    for (const key of dynamicPowerFactorKeys.value) {
      row[key] = point[key] ?? null
    }
    return row
  })
})

// Configuração das séries para os gráficos
const ALL_CURRENT_SERIES = computed(() => [
  { name: t('sensor.console.series.current_a'), key: 'current_a', color: '#ef4444' },
  { name: t('sensor.console.series.current_b'), key: 'current_b', color: '#f59e0b' },
  { name: t('sensor.console.series.current_c'), key: 'current_c', color: '#10b981' },
])

const ALL_VOLTAGE_SERIES = computed(() => [
  { name: t('sensor.console.series.voltage_a'), key: 'voltage_a', color: '#3b82f6' },
  { name: t('sensor.console.series.voltage_b'), key: 'voltage_b', color: '#8b5cf6' },
  { name: t('sensor.console.series.voltage_c'), key: 'voltage_c', color: '#ec4899' },
])

const ALL_POWER_FACTOR_SERIES = computed(() => [
  { name: t('sensor.console.series.power_factor'), key: 'power_factor', color: '#06b6d4' },
  { name: t('sensor.console.series.power_factor_a'), key: 'power_factor_a', color: '#14b8a6' },
  { name: t('sensor.console.series.power_factor_b'), key: 'power_factor_b', color: '#0ea5e9' },
  { name: t('sensor.console.series.power_factor_c'), key: 'power_factor_c', color: '#6366f1' },
])

const currentSeries = computed(() =>
  filterSeriesByAvailableKeys(ALL_CURRENT_SERIES.value, dynamicCurrentKeys.value),
)

const voltageSeries = computed(() =>
  filterSeriesByAvailableKeys(ALL_VOLTAGE_SERIES.value, dynamicVoltageKeys.value),
)

const powerFactorSeries = computed(() =>
  filterSeriesByAvailableKeys(ALL_POWER_FACTOR_SERIES.value, dynamicPowerFactorKeys.value),
)

const hasCurrentChart = computed(
  () => historicalCurrentData.value.length > 0 && currentSeries.value.length > 0,
)
const hasVoltageChart = computed(
  () => historicalVoltageData.value.length > 0 && voltageSeries.value.length > 0,
)
const hasPowerFactorChart = computed(
  () => historicalPowerFactorData.value.length > 0 && powerFactorSeries.value.length > 0,
)

/** Grade/cards sem mini-gráfico: preenche a altura do container (evita vazio cinza). */
const consoleFixedValuesGridClass =
  'grid h-full min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:gap-3'
const consoleFixedValueCardClass =
  'relative flex h-full min-h-[5.5rem] w-full flex-col items-center justify-center border border-gray-200 bg-white p-3 text-center shadow-sm transition-all'
/** Coluna de cards ao lado do mini-gráfico: distribui na altura do gráfico (não centraliza). */
const consoleChartSideCardsGridClass =
  'grid h-full min-h-0 grid-cols-1 auto-rows-fr gap-1.5 sm:col-span-1 lg:col-span-1'
const consoleChartSideCardClass =
  'relative flex h-full w-full flex-col items-center justify-center border border-gray-200 bg-white p-2 text-center shadow-sm transition-all'
const consoleFixedBadgeClass =
  'absolute top-1.5 right-1.5 rounded-none bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-gray-700'
const consoleFixedCardTitleClass = 'mb-1 px-1 text-center text-xs font-medium leading-snug text-gray-500'

/** Setpoint de temperatura normalizado para Number | null (API pode retornar string) */
const temperatureSetpointNumber = computed(() => {
  const raw = sensorDetails.value?.temperature_setpoint
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
})

const canEditSensor = computed(() => {
  const userRole = userStore.getCurrentUser?.role
  // Verifica se é manager (admin não existe no enum, mas pode vir como string)
  if (!userRole) return false
  if (userRole === UserRoles.MANAGER) return true
  const roleStr = String(userRole)
  return roleStr === 'admin' || roleStr === 'manager'
})

const sensorType = computed(() => sensorDetails.value?.sensor_type)
const sensorSchema = computed(() => sensorDetails.value?.schema_sensor)

/** Tipo do sensor no stream (enviado pela API) ou do detalhe; define como exibir o Console */
const effectiveRealtimeSensorType = computed(() => {
  const fromStream = readings.value?.sensor_type
  if (fromStream && ['electricity', 'temperature', 'door_open', 'rotation'].includes(String(fromStream))) {
    return String(fromStream)
  }
  return sensorDetails.value?.sensor_type || 'electricity'
})

// Verifica se o sensor tem schema
const hasSchema = computed(() => {
  return !!(sensorDetails.value?.schema_sensor?.schema && Object.keys(sensorDetails.value.schema_sensor.schema).length > 0)
})

const PHASE_PHASE_SCHEMA_KEYS = ['voltage_ab', 'voltage_bc', 'voltage_ca'] as const

const hasPhasePhaseSchemaKeys = computed(() => {
  const schema = sensorSchema.value?.schema
  if (!schema || typeof schema !== 'object') return false
  return PHASE_PHASE_SCHEMA_KEYS.some((key) => key in schema)
})

// Campos que são metadados do stream (não exibir como dado do sensor)
const REALTIME_META_KEYS = ['sensor_type']

const ELECTRICITY_KEYS = ['current_a', 'current_b', 'current_c', 'voltage_a', 'voltage_b', 'voltage_c', 'voltage_ab', 'voltage_bc', 'voltage_ca', 'power_factor', 'power_factor_a', 'power_factor_b', 'power_factor_c']

// Computed para dados em tempo real - mostra os campos do schema (sem sensor_type como dado)
const realtimeData = computed(() => {
  if (!sensorDetails.value) return {}

  const schema = sensorDetails.value.schema_sensor?.schema || {}
  const combined: Record<string, any> = {}
  const currentReadings = readings.value

  if (schema && typeof schema === 'object') {
    Object.keys(schema).forEach((key) => {
      if (REALTIME_META_KEYS.includes(key)) return
      combined[key] = currentReadings && currentReadings[key] !== undefined ? currentReadings[key] : null
    })
  }

  return combined
})

// Verifica se há algum dado recebido (não null/undefined)
const hasAnyData = computed(() => {
  if (effectiveRealtimeSensorType.value === 'door_open' && readings.value && Object.keys(readings.value).length > 0) return true
  const data = realtimeData.value
  return Object.values(data).some(value => value !== null && value !== undefined)
})

// Verifica se há grupos principais com dados (energia)
const hasGroupData = computed(() => {
  return (
    (realtimeData.value.current_a !== undefined || realtimeData.value.current_b !== undefined || realtimeData.value.current_c !== undefined) ||
    (realtimeData.value.voltage_a !== undefined || realtimeData.value.voltage_b !== undefined || realtimeData.value.voltage_c !== undefined) ||
    (realtimeData.value.power_factor !== undefined || realtimeData.value.power_factor_a !== undefined || realtimeData.value.power_factor_b !== undefined || realtimeData.value.power_factor_c !== undefined)
  )
})

// Dados recebidos por grupo (para shimmer: só mostrar conteúdo real quando valor válido chegou do broker)
// O realtimeData preenche com null os campos do schema; precisamos checar valor real (não null/undefined)
const hasValidNumeric = (v: unknown) => v != null && v !== '' && !Number.isNaN(Number(v))
const hasCurrentData = computed(() => {
  if (!showCurrentGroup.value) return false
  const d = realtimeData.value
  return (
    dynamicCurrentKeys.value.some((key) => hasValidNumeric(d[key])) ||
    historicalCurrentData.value.length > 0
  )
})
const hasVoltageData = computed(() => {
  if (!showVoltageGroup.value) return false
  const d = realtimeData.value
  return (
    availableVoltageKeys.value.some((key) => hasValidNumeric(d[key]) || isFieldFixed(key)) ||
    hasFixedVoltageValues.value ||
    historicalVoltageData.value.length > 0
  )
})
const hasPowerFactorData = computed(() => {
  if (!showPowerFactorGroup.value) return false
  const d = realtimeData.value
  return (
    availablePowerFactorKeys.value.some((key) => hasValidNumeric(d[key]) || isFieldFixed(key)) ||
    hasFixedPowerFactorValues.value ||
    historicalPowerFactorData.value.length > 0
  )
})

function isFieldFixed(key: string): boolean {
  return isFixedValueKey(key, fixedValues.value)
}

// Temperatura ou abertura com dado no stream
const hasRealtimeTemperature = computed(() => effectiveRealtimeSensorType.value === 'temperature' && (realtimeData.value.temperature !== undefined && realtimeData.value.temperature !== null))
const hasRealtimeDoorOpen = computed(() => {
  if (effectiveRealtimeSensorType.value !== 'door_open') return false
  return !!readings.value?.door_status || Object.keys(readings.value || {}).some(k => k !== 'door_status' && !REALTIME_META_KEYS.includes(k))
})

function isOtherRealtimeField(key: string): boolean {
  if (ELECTRICITY_KEYS.includes(key) || REALTIME_META_KEYS.includes(key)) return false
  if (effectiveRealtimeSensorType.value === 'temperature' && key === 'temperature') return false
  if (effectiveRealtimeSensorType.value === 'door_open') return false
  return true
}

const showOtherRealtimeFields = computed(() =>
  Object.keys(realtimeData.value).some((key) => isOtherRealtimeField(key)),
)

const hasInterruptionAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_interruption_alarm ?? false)
const hasLowLoadAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_low_load_alarm ?? false)
const hasDefrostTimeAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_defrost_time_alarm ?? false)
const hasBurntResistor = computed(() => sensorDetails.value?.company_alarm_status?.active_burnt_resistor_alarm ?? false)
const hasDoorOpen = computed(() => sensorDetails.value?.company_alarm_status?.active_door_open_alarm ?? false)
const hasPeakHourAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_peak_hour_alarm ?? false)
const hasNoDataAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_no_data_alarm ?? false)
const hasTemperatureAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_temperature_alarm ?? false)
const hasEmptyMixerAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_empty_mixer_alarm ?? false)
const hasOutsideRegularTimeAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_outside_regular_time_alarm ?? false)
const hasPowerFactorAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_power_factor_alarm ?? false)
const hasOvervoltageAlarm = computed(() => sensorDetails.value?.company_alarm_status?.active_overvoltage_alarm ?? false)

const tabs = ['console', 'info', 'schema', 'settings'] as const
type TabType = typeof tabs[number]
const activeTab = ref<TabType>(tabs[0])

const loadSensorDetails = async () => {
  isLoading.value = true

  stopPolling()
  readings.value = {}
  historicalData.value = []
  fixedValues.value = {}
  dynamicCurrentKeys.value = []
  dynamicVoltageKeys.value = []
  dynamicPowerFactorKeys.value = []
  hasMiniChartSeedCompleted.value = false
  lastHistoryTimestampMs.value = null
  lastDataSentAt.value = null
  pollingError.value = null

  try {
    const id = sensorId.value
    if (!id || isNaN(id)) {
      router.push({ name: 'list-sensors' })
      return
    }

    let sensor = store.listSensors.find((s) => s.id === id)
    if (!sensor) {
      await store.fetch({ force: true })
      sensor = store.listSensors.find((s) => s.id === id)
      if (!sensor) {
        router.push({ name: 'list-sensors' })
        return
      }
    }

    const sensorTypeForDetail = sensor.sensor_type || 'electricity'
    sensorDetails.value = await store.fetchSensorDetails(id, sensorTypeForDetail)

    // Garante que a store tenha o sensor selecionado para AlarmsConfig e outros componentes
    if (sensorDetails.value) {
      store.selectedSensorDetails = sensorDetails.value
      const sensorFromList = store.listSensors.find((s) => s.id === id)
      store.selectedSensor = sensorFromList ?? null
    }

    // Schemas do mesmo tipo do sensor (empresa = Privado, is_public_schema = Público)
    if (sensorDetails.value) {
      schemaOptions.value = await store.fetchSchemas(sensorDetails.value.sensor_type)

      const current = sensorSchema.value
      if (current) {
        const found = schemaOptions.value.find((o) => Number(o.id) === Number(current.id ?? current.schema?.id))
        if (!found) {
          schemaOptions.value = [current, ...schemaOptions.value]
        }
        selectedSchema.value = found ?? current
      } else {
        selectedSchema.value = null
      }
    }

    if (sensorDetails.value && id) {
      if (sensorDetails.value.sensor_type === 'electricity') {
        await seedMiniChartHistoryFromReadings()
      }
      startPolling()
    }
  } catch {
    router.push({ name: 'list-sensors' })
  } finally {
    isLoading.value = false
  }
}

const saveSchema = async () => {
  if (!sensorDetails.value || !selectedSchema.value) return
  await store.assignSchemaToSensor(selectedSchema.value.id, sensorDetails.value.id)
  await loadSensorDetails()
}

const openEditModal = () => {
  isEditModalOpen.value = true
}

const closeEditModal = () => {
  isEditModalOpen.value = false
}

const onSensorSaved = async () => {
  await loadSensorDetails()
  closeEditModal()
}

const formatValue = (key: string, value: any): string => {
  if (value === null || value === undefined) return '-'

  // Formatação específica por tipo de dado
  if (typeof value === 'number') {
    // Se for corrente ou tensão, adiciona unidade
    if (key.toLowerCase().includes('current') || key.toLowerCase().includes('corrente')) {
      return `${value.toFixed(0)} A`
    }
    if (key.toLowerCase().includes('voltage') || key.toLowerCase().includes('tensão')) {
      return `${value.toFixed(0)} V`
    }
    if (key.toLowerCase().includes('power') || key.toLowerCase().includes('potência')) {
      return `${value.toFixed(2)}`
    }
    if (key.toLowerCase().includes('temperature') || key.toLowerCase().includes('temperatura')) {
      return `${value.toFixed(1)} °C`
    }
    return value.toFixed(2)
  }

  return String(value)
}

const getFieldLabel = (key: string): string => {
  const labelKey = `sensor.fields.${key}`
  const translated = t(labelKey)
  if (translated !== labelKey) return translated

  return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

// Watch para reagir a mudanças no sensorId (navegação entre sensores)
watch(sensorId, (newId) => {
  stopPolling()

  if (newId && !isNaN(newId)) {
    loadSensorDetails()
  }
}, { immediate: false })

watch(pollingIntervalSeconds, () => {
  if (!sensorDetails.value || !sensorDetails.value.id) return
  startPolling()
})

onMounted(() => {
  fetchCompanyAlarmTypes()
  isComponentMounted.value = true
  relativeTimeTimer = setInterval(() => {
    nowTimestamp.value = Date.now()
  }, 1000)

  if (sensorId.value && !isNaN(sensorId.value)) {
    loadSensorDetails()
  } else {
    router.push({ name: 'list-sensors' })
  }
})

onBeforeUnmount(() => {
  // Marca componente como desmontado para evitar atualizações
  isComponentMounted.value = false

  stopPolling()
  if (relativeTimeTimer) {
    clearInterval(relativeTimeTimer)
    relativeTimeTimer = null
  }
})

// ───────────────────────────── Ativar / Desativar sensor ─────────────────────────────

const isSensorArchived = computed(() => Boolean(sensorDetails.value?.archived_at))

const isArchiveModalOpen = ref(false)
const isUnarchiveModalOpen = ref(false)
const isArchiveBusy = ref(false)

function openArchiveModal() {
  isArchiveModalOpen.value = true
}

function openUnarchiveModal() {
  isUnarchiveModalOpen.value = true
}

function closeArchiveModal() {
  if (isArchiveBusy.value) return
  isArchiveModalOpen.value = false
}

function closeUnarchiveModal() {
  if (isArchiveBusy.value) return
  isUnarchiveModalOpen.value = false
}

async function confirmArchive() {
  if (!sensorDetails.value || isArchiveBusy.value) return
  isArchiveBusy.value = true
  try {
    await store.archiveSensor(sensorDetails.value.id)
    // Sincroniza o detalhe local com o estado pós-archive (encerra o polling
    // de leituras pra não tentar bater no Influx de um sensor desativado).
    sensorDetails.value.archived_at = new Date().toISOString()
    stopPolling()
    isArchiveModalOpen.value = false
  } finally {
    isArchiveBusy.value = false
  }
}

async function confirmUnarchive() {
  if (!sensorDetails.value || isArchiveBusy.value) return
  isArchiveBusy.value = true
  try {
    await store.unarchiveSensor(sensorDetails.value.id)
    sensorDetails.value.archived_at = null
    isUnarchiveModalOpen.value = false
  } finally {
    isArchiveBusy.value = false
  }
}
</script>

<template>
  <DefaultLayout>
    <template v-if="isLoading">
      <Loader />
    </template>

    <template v-else-if="sensorDetails && sensorDetails.id && !isLoading">
      <div class="mx-auto max-w-[98%] px-2 py-4">


        <!-- Cabeçalho do Sensor -->
        <div class="mb-4 bg-white p-6 shadow-lg">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <!-- Linha superior: Voltar + Nome + Badge + Editar -->
              <div class="mb-4 flex items-center gap-3">
                <button
                  @click="router.back()"
                  class="flex items-center justify-center p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                  :title="t('sensor.detail.back_to_list')">
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <h1 class="text-2xl font-bold text-gray-900">{{ sensorDetails.name }}</h1>

                <span :class="`px-2.5 py-1 text-xs font-medium text-white ${getSensorColor(sensorDetails.sensor_type)}`">
                  {{ translateSensorType(sensorDetails.sensor_type) }}
                </span>
              </div>

              <!-- Informações do sensor -->
              <div class="flex flex-wrap gap-2">
                <div class="min-w-0 flex-shrink-0 border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-2.5 shadow-sm transition-all hover:shadow-md hover:border-blue-300">
                  <div class="flex items-center gap-1.5 mb-1">
                    <svg class="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <p class="text-xs font-semibold uppercase tracking-wide text-gray-600">{{ t('sensor.detail.mac_address') }}</p>
                  </div>
                  <p class="text-base font-bold text-gray-900">{{ sensorDetails.mac_address }}</p>
                </div>

                <div class="min-w-0 flex-shrink-0 border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-2.5 shadow-sm transition-all hover:shadow-md hover:border-blue-300">
                  <div class="flex items-center gap-1.5 mb-1">
                    <svg class="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                    </svg>
                    <p class="text-xs font-semibold uppercase tracking-wide text-gray-600">{{ t('sensor.detail.subid') }}</p>
                  </div>
                  <p class="text-base font-bold text-gray-900">{{ sensorDetails.subid }}</p>
                </div>

                <div
                  class="min-w-0 flex-shrink-0 border p-2.5 shadow-sm transition-all hover:shadow-md"
                  :class="isSensorArchived
                    ? 'border-slate-300 bg-gradient-to-br from-slate-100 to-white hover:border-slate-400'
                    : 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-400'">
                  <div class="flex items-center gap-1.5 mb-1">
                    <svg class="h-3 w-3" :class="isSensorArchived ? 'text-slate-500' : 'text-emerald-600'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p class="text-xs font-semibold uppercase tracking-wide" :class="isSensorArchived ? 'text-slate-600' : 'text-emerald-700'">{{ t('sensor.detail.status') }}</p>
                  </div>
                  <p class="text-base font-bold" :class="isSensorArchived ? 'text-slate-700' : 'text-emerald-700'">
                    {{ isSensorArchived ? t('sensor.detail.status_inactive') : t('sensor.detail.status_active') }}
                  </p>
                  <p v-if="isSensorArchived && sensorDetails.archived_at" class="mt-0.5 text-[10px] text-slate-500">
                    {{ t('sensor.detail.archived_since', { date: new Date(sensorDetails.archived_at).toLocaleDateString('pt-BR') }) }}
                  </p>
                </div>

              </div>
            </div>

            <!-- Ações no canto superior direito -->
            <div class="ml-4 flex items-start gap-3">
              <!-- Toggle Ativo/Desativado -->
              <div class="flex items-center gap-2">
                <span
                  class="text-xs font-semibold uppercase tracking-wide"
                  :class="isSensorArchived ? 'text-slate-500' : 'text-emerald-700'">
                  {{ isSensorArchived ? t('sensor.detail.status_inactive') : t('sensor.detail.status_active') }}
                </span>
                <button
                  type="button"
                  role="switch"
                  :aria-checked="!isSensorArchived"
                  :disabled="isArchiveBusy || !canEditSensor"
                  :title="!canEditSensor
                    ? t('sensor.detail.toggle_archive_forbidden')
                    : (isSensorArchived ? t('sensor.detail.toggle_unarchive') : t('sensor.detail.toggle_archive'))"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  :class="isSensorArchived ? 'bg-slate-300 focus:ring-slate-400' : 'bg-emerald-500 focus:ring-emerald-400'"
                  @click="isSensorArchived ? openUnarchiveModal() : openArchiveModal()">
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200"
                    :class="isSensorArchived ? 'translate-x-0.5' : 'translate-x-[1.375rem]'" />
                </button>
              </div>

              <button
                v-if="canEditSensor"
                class="bg-gray-400 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-500"
                @click="openEditModal">
                {{ t('sensor.detail.edit_sensor') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Tabs no topo -->
        <div class="mb-6 bg-white shadow-lg">
          <div class="border-b border-gray-200">
            <nav class="-mb-px flex space-x-8 px-6" :aria-label="t('sensor.detail.tabs.nav_label')">
              <button
                v-for="tab in tabs"
                :key="tab"
                @click="activeTab = tab"
                :class="[
                  'border-b-2 py-4 px-1 text-sm font-medium transition-colors',
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                ]">
                {{ t(`sensor.detail.tabs.${tab}`) }}
              </button>
            </nav>
          </div>

          <div class="p-6">
            <!-- Aba Console (v-show para preservar estado do gráfico ao trocar de aba) -->
            <div v-show="activeTab === 'console'">
              <!-- Erro se não tiver schema -->
              <div v-if="!hasSchema" class="bg-red-50 border-2 border-red-200 p-6">
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div class="ml-3 flex-1">
                    <h3 class="text-lg font-medium text-red-800">{{ t('sensor.console.schema_missing_title') }}</h3>
                    <p class="mt-2 text-sm text-red-700">
                      {{ t('sensor.console.schema_missing_body') }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Dashboard com dados em tempo real -->
              <template v-else>
              <div
                v-if="sensorDetails?.sensor_type === 'electricity'"
                class="mb-6">
                <LastCommunicationCard
                  class="min-w-0 w-full"
                  :heading="t('sensor.console.last_sensor_send')"
                  :formatted-last-data-sent-at="formattedLastDataSentAt"
                  :last-data-age-label="lastDataAgeLabel"
                  v-model:polling-interval-seconds="pollingIntervalSeconds"
                  :polling-interval-options="pollingIntervalOptions"
                  :polling-error="pollingError"
                />
              </div>

              <!-- Grupos: 1 col (mobile) → 2 cols (md) → 3 cols (2xl); gaps responsivos -->
              <div class="mb-6 grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-3 sm:gap-4 2xl:gap-6">
                <!-- Grupo: Corrente (oculto se schema não expõe corrente em readings/fixed) -->
                <RealtimeGroupCard
                  v-if="sensorDetails?.sensor_type === 'electricity' && showCurrentGroup"
                  :title="t('sensor.console.group_current')"
                  indicator-color="bg-red-500"
                  :is-connected="isPolling">
                  <!-- Shimmer: aguardando dado do broker -->
                  <ShimmerRealtimeGroup v-if="!hasCurrentData" :indicator-count="3" />
                  <!-- Conteúdo real quando há dado -->
                  <div v-else class="flex h-full flex-col">
                    <div
                      v-if="hasCurrentChart"
                      class="grid min-h-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-3 lg:gap-3">
                      <div class="border border-gray-200 bg-white p-1.5 sm:col-span-1 sm:p-2 lg:col-span-2">
                        <RealtimeChart
                          :title="t('sensor.console.history_current')"
                          :data="historicalCurrentData"
                          :series="currentSeries"
                          unit="A"
                        />
                      </div>
                      <div :class="consoleChartSideCardsGridClass">
                        <div
                          v-for="(value, key) in currentCardEntries"
                          :key="key"
                          :class="[consoleChartSideCardClass, 'hover:border-red-300 hover:shadow-md']">
                          <div v-if="value !== null && value !== undefined" class="absolute top-1 right-1">
                            <div class="h-1.5 w-1.5 animate-pulse bg-green-500"></div>
                          </div>
                          <p class="mb-0.5 text-xs font-medium text-gray-500">{{ getFieldLabel(String(key)) }}</p>
                          <p :class="['text-base font-bold', value === null || value === undefined ? 'text-gray-400' : 'text-gray-900']">
                            {{ formatValue(String(key), value) }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div v-else :class="consoleFixedValuesGridClass">
                      <div
                        v-for="(value, key) in currentCardEntries"
                        :key="key"
                        :class="[consoleFixedValueCardClass, 'hover:border-red-300 hover:shadow-md']">
                        <div v-if="value !== null && value !== undefined" class="absolute top-1.5 right-1.5">
                          <div class="h-1.5 w-1.5 animate-pulse bg-green-500"></div>
                        </div>
                        <p class="mb-0.5 text-xs font-medium text-gray-500">{{ getFieldLabel(String(key)) }}</p>
                        <p :class="['text-base font-bold', value === null || value === undefined ? 'text-gray-400' : 'text-gray-900']">
                          {{ formatValue(String(key), value) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </RealtimeGroupCard>

                <!-- Grupo: Tensão (oculto se não houver chaves em readings nem fixed_values) -->
                <RealtimeGroupCard
                  v-if="sensorDetails?.sensor_type === 'electricity' && showVoltageGroup"
                  :title="t('sensor.console.group_voltage')"
                  indicator-color="bg-blue-500"
                  :is-connected="isPolling">
                  <!-- Shimmer: aguardando dado do broker -->
                  <ShimmerRealtimeGroup v-if="!hasVoltageData" :indicator-count="3" />
                  <!-- Conteúdo real quando há dado -->
                  <div v-else class="flex h-full flex-col">
                    <div
                      v-if="hasVoltageChart"
                      class="grid min-h-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-3 lg:gap-3">
                      <div class="border border-gray-200 bg-white p-1.5 sm:col-span-1 sm:p-2 lg:col-span-2">
                        <RealtimeChart
                          :title="t('sensor.console.history_voltage')"
                          :data="historicalVoltageData"
                          :series="voltageSeries"
                          unit="V"
                        />
                      </div>
                      <div :class="consoleChartSideCardsGridClass">
                        <div
                          v-for="(value, key) in voltageCardEntries"
                          :key="key"
                          :class="[consoleChartSideCardClass, 'hover:border-blue-300 hover:shadow-md']">
                          <div v-if="value !== null && value !== undefined && !isFieldFixed(String(key))" class="absolute top-1.5 right-1.5">
                            <div class="h-1.5 w-1.5 animate-pulse bg-green-500"></div>
                          </div>
                          <span v-if="isFieldFixed(String(key))" :class="consoleFixedBadgeClass">
                            {{ t('sensor.console.fixed_badge') }}
                          </span>
                          <p :class="consoleFixedCardTitleClass">{{ getFieldLabel(String(key)) }}</p>
                          <p :class="['text-base font-bold', value === null || value === undefined ? 'text-gray-400' : 'text-gray-900']">
                            {{ formatValue(String(key), value) }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div v-else :class="consoleFixedValuesGridClass">
                      <div
                        v-for="(value, key) in voltageCardEntries"
                        :key="key"
                        :class="[consoleFixedValueCardClass, 'hover:border-blue-300 hover:shadow-md']">
                        <div v-if="value !== null && value !== undefined && !isFieldFixed(String(key))" class="absolute top-1.5 right-1.5">
                          <div class="h-1.5 w-1.5 animate-pulse bg-green-500"></div>
                        </div>
                        <span v-if="isFieldFixed(String(key))" :class="consoleFixedBadgeClass">
                          {{ t('sensor.console.fixed_badge') }}
                        </span>
                        <p :class="consoleFixedCardTitleClass">{{ getFieldLabel(String(key)) }}</p>
                        <p :class="['text-base font-bold', value === null || value === undefined ? 'text-gray-400' : 'text-gray-900']">
                          {{ formatValue(String(key), value) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </RealtimeGroupCard>

                <!-- Grupo: Fator de Potência (mesma regra genérica: readings ∪ fixed_values) -->
                <RealtimeGroupCard
                  v-if="sensorDetails?.sensor_type === 'electricity' && showPowerFactorGroup"
                  :title="t('sensor.console.group_power_factor')"
                  indicator-color="bg-cyan-500"
                  :is-connected="isPolling">
                  <!-- Shimmer: aguardando dado do broker -->
                  <ShimmerRealtimeGroup
                    v-if="!hasPowerFactorData"
                    :indicator-count="4"
                    label-width="w-24"
                    value-width="w-20"
                  />
                  <!-- Conteúdo real quando há dado -->
                  <div v-else class="flex h-full flex-col">
                    <div
                      v-if="hasPowerFactorChart"
                      class="grid min-h-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-3 lg:gap-3">
                      <div class="border border-gray-200 bg-white p-1.5 sm:col-span-1 sm:p-2 lg:col-span-2">
                        <RealtimeChart
                          :title="t('sensor.console.history_power_factor')"
                          :data="historicalPowerFactorData"
                          :series="powerFactorSeries"
                          unit=""
                        />
                      </div>
                      <div :class="consoleChartSideCardsGridClass">
                        <div
                          v-for="(value, key) in powerFactorCardEntries"
                          :key="key"
                          :class="[consoleChartSideCardClass, 'hover:border-cyan-300 hover:shadow-md']">
                          <div v-if="value !== null && value !== undefined && !isFieldFixed(String(key))" class="absolute top-1.5 right-1.5">
                            <div class="h-1.5 w-1.5 animate-pulse bg-green-500"></div>
                          </div>
                          <span v-if="isFieldFixed(String(key))" :class="consoleFixedBadgeClass">
                            {{ t('sensor.console.fixed_badge') }}
                          </span>
                          <p :class="consoleFixedCardTitleClass">{{ getFieldLabel(String(key)) }}</p>
                          <p :class="['text-base font-bold', value === null || value === undefined ? 'text-gray-400' : 'text-gray-900']">
                            {{ formatValue(String(key), value) }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div v-else :class="consoleFixedValuesGridClass">
                      <div
                        v-for="(value, key) in powerFactorCardEntries"
                        :key="key"
                        :class="[consoleFixedValueCardClass, 'hover:border-cyan-300 hover:shadow-md']">
                        <div v-if="value !== null && value !== undefined && !isFieldFixed(String(key))" class="absolute top-1.5 right-1.5">
                          <div class="h-1.5 w-1.5 animate-pulse bg-green-500"></div>
                        </div>
                        <span v-if="isFieldFixed(String(key))" :class="consoleFixedBadgeClass">
                          {{ t('sensor.console.fixed_badge') }}
                        </span>
                        <p :class="consoleFixedCardTitleClass">{{ getFieldLabel(String(key)) }}</p>
                        <p :class="['text-base font-bold', value === null || value === undefined ? 'text-gray-400' : 'text-gray-900']">
                          {{ formatValue(String(key), value) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </RealtimeGroupCard>
              </div>

              <div
                v-if="sensorDetails?.sensor_type === 'electricity' && hasAnyFixedConsoleValues"
                class="mb-6 flex items-center justify-center gap-2 rounded-none border border-stroke bg-gray-50 p-3 text-center">
                <InformationCircleIcon class="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
                <p class="text-sm font-medium text-gray-600">
                  {{ t('sensor.console.fixed_values_banner') }}
                </p>
              </div>

              <!-- Bloco: Energia — gráfico de corrente e alarmes encontrados no período -->
              <RealtimeGroupCard
                v-if="sensorDetails?.sensor_type === 'electricity'"
                :title="t('sensor.console.group_energy')"
                indicator-color="bg-red-500"
                :is-connected="isPolling"
                content-class="p-2 md:p-5"
                class="mb-6">
                <EnergyCurrentChart
                  v-if="sensorId"
                  :sensor-id="sensorId"
                />
                <EnergyVoltageChart
                  v-if="sensorId"
                  class="mt-6"
                  :sensor-id="sensorId"
                  :nominal-voltage="sensorDetails?.nominal_voltage ?? null"
                  :phase-phase-keys-configured="hasPhasePhaseSchemaKeys"
                />
              </RealtimeGroupCard>

              <RealtimeGroupCard
                v-if="sensorDetails?.sensor_type === 'temperature' || hasRealtimeTemperature"
                :title="t('sensor.console.group_temperature')"
                indicator-color="bg-darkGreen"
                :is-connected="isPolling"
                content-class="p-2 md:p-5"
                class="mb-6">
                <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <TemperatureStatusCard
                    v-if="sensorDetails?.sensor_type === 'temperature'"
                    :value="realtimeData.temperature"
                    :setpoint="temperatureSetpointNumber"
                    :max-bar="60"
                  />
                  <LastCommunicationCard
                    v-if="sensorDetails?.sensor_type === 'temperature'"
                    :formatted-last-data-sent-at="formattedLastDataSentAt"
                    :last-data-age-label="lastDataAgeLabel"
                    v-model:polling-interval-seconds="pollingIntervalSeconds"
                    :polling-interval-options="pollingIntervalOptions"
                    :polling-error="pollingError"
                  />
                </div>
                <div class="mt-8 md:mt-10">
                <TemperatureHybridChart
                  v-if="sensorDetails?.sensor_type === 'temperature' && sensorId"
                  :sensor-id="sensorId"
                  :setpoint="temperatureSetpointNumber"
                />
                </div>
              </RealtimeGroupCard>

              <!-- Bloco: Abertura de porta (quando o stream indica sensor_type door_open) -->
              <RealtimeGroupCard
                v-if="hasRealtimeDoorOpen"
                :title="t('sensor.console.group_door_open')"
                indicator-color="bg-orange-500"
                :is-connected="isPolling"
                class="mb-6">
                <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div class="sm:col-span-1 lg:col-span-1 flex flex-col gap-1.5">
                    <div class="relative border border-gray-200 bg-white p-2 shadow-sm transition-all hover:shadow-md hover:border-orange-300">
                      <div class="absolute top-1 right-1">
                        <div class="h-1.5 w-1.5 bg-green-500 animate-pulse"></div>
                      </div>
                      <p class="text-xs font-medium text-gray-500 mb-0.5">Status da Porta</p>
                      <p :class="['text-base font-bold', readings.door_status === 'open' ? 'text-red' : 'text-yellow-400']">
                        {{
                          readings.door_status === 'open'
                            ? t('sensor.console.door_open')
                            : readings.door_status === 'closed'
                              ? t('sensor.console.door_closed')
                              : '-'
                        }}
                      </p>
                    </div>
                  </div>

                  <LastCommunicationCard
                    v-if="sensorDetails?.sensor_type === 'door_open'"
                    :formatted-last-data-sent-at="formattedLastDataSentAt"
                    :last-data-age-label="lastDataAgeLabel"
                    v-model:polling-interval-seconds="pollingIntervalSeconds"
                    :polling-interval-options="pollingIntervalOptions"
                    :polling-error="pollingError"
                  />
                </div>

                <div class="mt-8 md:mt-10">
                  <DoorOpenChart
                    v-if="sensorDetails?.sensor_type === 'door_open' && sensorId"
                    :sensor-id="sensorId"
                    :alarm-door-open-profile-id="sensorDetails.alarm_door_open_sensor_id ?? null"
                  />
                </div>
              </RealtimeGroupCard>

              <!-- Outros campos que não se encaixam nos grupos (exclui sensor_type e campos já em cards específicos) -->
              <div v-if="showOtherRealtimeFields" class="mb-6">
                <h3 class="mb-3 text-lg font-semibold text-gray-900">{{ t('sensor.console.other_info') }}</h3>
                <div class="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                  <template
                    v-for="(value, key) in realtimeData"
                    :key="key">
                    <div
                      v-if="isOtherRealtimeField(key)"
                      class="relative border border-gray-200 bg-white p-3 shadow-sm transition-all hover:shadow-md hover:border-gray-300">
                      <div v-if="value !== null && value !== undefined" class="absolute top-1.5 right-1.5">
                        <div class="h-1.5 w-1.5 bg-green-500 animate-pulse"></div>
                      </div>
                      <p class="text-xs font-medium text-gray-500 mb-1">{{ getFieldLabel(String(key)) }}</p>
                      <p :class="['text-xl font-bold', value === null || value === undefined ? 'text-gray-400' : 'text-gray-900']">
                        {{ formatValue(String(key), value) }}
                      </p>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Mensagem quando não há dados ainda -->
              <div v-if="!hasGroupData && !hasRealtimeTemperature && !hasRealtimeDoorOpen && !hasAnyData && !isPolling" class="bg-gray-50 border border-gray-200 p-8 text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p class="mt-4 text-gray-600">{{ t('sensor.console.no_data_yet') }}</p>
                <p class="mt-2 text-sm text-gray-500">{{ t('sensor.console.waiting_influx') }}</p>
              </div>
              </template>
            </div>

            <!-- Aba Informações -->
            <div v-show="activeTab === 'info'">
              <template v-if="sensorDetails.sensor_type === 'electricity'">
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <CardInfo :title="t('sensor.detail.status')" :value="sensorDetails.status" />
                  <CardInfo :title="t('sensor.detail.info.nominal_current')" :value="sensorDetails.current" />
                  <CardInfo :title="t('sensor.detail.info.nominal_voltage')" :value="sensorDetails.nominal_voltage ?? '-'" />
                  <CardInfo :title="t('sensor.detail.info.utilization_current')" :value="sensorDetails.min_utilization_current" />
                  <CardInfo :title="t('sensor.detail.info.min_non_utilization_current')" :value="sensorDetails.min_non_utilization_current" />
                  <CardInfo :title="t('sensor.detail.info.manufacturer')" :value="sensorDetails.manufacturer" />
                  <CardInfo :title="t('sensor.detail.info.data_source')" :value="sensorDetails.data_source" />
                  <CardInfo :title="t('sensor.detail.info.current_ratio_a')" :value="sensorDetails.current_calculation_ratio_phase_a ?? '-'" />
                  <CardInfo :title="t('sensor.detail.info.current_ratio_b')" :value="sensorDetails.current_calculation_ratio_phase_b ?? '-'" />
                  <CardInfo :title="t('sensor.detail.info.current_ratio_c')" :value="sensorDetails.current_calculation_ratio_phase_c ?? '-'" />
                  <CardInfo :title="t('sensor.detail.info.voltage_calculation_ratio')" :value="sensorDetails.voltage_calculation_ratio ?? '-'" />
                  <CardInfo
                    :title="t('sensor.detail.info.inverted_signal')"
                    :value="sensorDetails.inverted_signal ? t('sensor.detail.info.yes') : t('sensor.detail.info.no')" />
                </div>
                <p class="mt-4 text-sm text-gray-500">
                  {{ t('sensor.detail.info.edit_current_hint') }}
                </p>
              </template>

              <template v-else-if="sensorDetails.sensor_type === 'door_open'">
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <CardInfo :title="t('sensor.detail.status')" :value="sensorDetails.status" />
                  <CardInfo :title="t('sensor.detail.info.manufacturer')" :value="sensorDetails.manufacturer" />
                  <CardInfo :title="t('sensor.detail.info.voltage_field')" :value="sensorDetails.voltage_field" />
                </div>
              </template>

              <template v-else-if="sensorDetails.sensor_type === 'temperature'">
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <CardInfo :title="t('sensor.detail.status')" :value="sensorDetails.status" />
                  <CardInfo :title="t('sensor.detail.info.manufacturer')" :value="sensorDetails.manufacturer" />
                  <CardInfo :title="t('sensor.fields.critical_temperature_c')" :value="sensorDetails.temperature_setpoint" />
                  <CardInfo :title="t('sensor.detail.info.temperature_divisor')" :value="sensorDetails.temperature_value_divisor ?? 10" />
                </div>
                <p class="mt-4 text-sm text-gray-500">
                  {{ t('sensor.detail.info.edit_hint') }}
                </p>
              </template>
            </div>

            <!-- Aba Modelo -->
            <div v-show="activeTab === 'schema'">
              <div class="space-y-6">
                <div class="bg-gray-50 p-4 border border-gray-200">
                  <h2 class="mb-2 text-base font-bold text-gray-800">
                    {{ sensorSchema ? t('sensor.detail.schema.in_use_title') : t('sensor.detail.schema.assign_title') }}
                  </h2>
                  <p v-if="sensorSchema" class="mb-2 text-sm text-gray-600">
                    {{ t('sensor.detail.schema.in_use_body', { name: sensorSchema.name }) }}
                  </p>
                  <Dropdown
                    name="schemaSelect"
                    labelAttribute="name"
                    :placeholder="t('sensor.detail.schema.placeholder')"
                    :options="schemaOptions"
                    :alreadySelectedOption="selectedSchema"
                    @option-selected="(opt: ISchemaSensor | null) => selectedSchema = opt || null"
                  >
                    <!-- Opções filtradas por tipo do sensor: da empresa = Privado, is_public_schema = Público -->
                    <template #option="{ option }">
                      <span class="flex items-center gap-2">
                        <span>{{ option.name }}</span>
                        <span
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                          :class="option.is_public_schema ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'">
                          {{ option.is_public_schema ? t('sensor.detail.schema.public') : t('sensor.detail.schema.private') }}
                        </span>
                      </span>
                    </template>
                  </Dropdown>
                </div>

                <div v-if="previewSchema" class="space-y-4">
                  <h3 class="text-lg font-semibold text-gray-900">{{ t('sensor.detail.schema.fields_title') }}</h3>

                  <template v-if="sensorDetails.sensor_type === 'electricity'">
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <CardInfo :title="t('sensor.fields.current_a')" :value="previewSchema.current_a" />
                      <CardInfo :title="t('sensor.fields.current_b')" :value="previewSchema.current_b" />
                      <CardInfo :title="t('sensor.fields.current_c')" :value="previewSchema.current_c" />
                      <CardInfo :title="t('sensor.fields.voltage_a')" :value="previewSchema.voltage_a" />
                      <CardInfo :title="t('sensor.fields.voltage_b')" :value="previewSchema.voltage_b" />
                      <CardInfo :title="t('sensor.fields.voltage_c')" :value="previewSchema.voltage_c" />
                      <CardInfo :title="t('sensor.fields.voltage_ab')" :value="previewSchema.voltage_ab" />
                      <CardInfo :title="t('sensor.fields.voltage_bc')" :value="previewSchema.voltage_bc" />
                      <CardInfo :title="t('sensor.fields.voltage_ca')" :value="previewSchema.voltage_ca" />
                      <CardInfo :title="t('sensor.fields.power_factor_a')" :value="previewSchema.power_factor_a" />
                      <CardInfo :title="t('sensor.fields.power_factor_b')" :value="previewSchema.power_factor_b" />
                      <CardInfo :title="t('sensor.fields.power_factor_c')" :value="previewSchema.power_factor_c" />
                      <CardInfo :title="t('sensor.fields.power_factor_short')" :value="previewSchema.power_factor" />
                    </div>
                  </template>

                  <template v-else-if="sensorDetails.sensor_type === 'door_open'">
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <CardInfo :title="t('sensor.fields.current_a')" :value="previewSchema.current_a" />
                      <CardInfo :title="t('sensor.fields.current_b')" :value="previewSchema.current_b" />
                      <CardInfo :title="t('sensor.fields.current_c')" :value="previewSchema.current_c" />
                      <CardInfo :title="t('sensor.fields.door_open_voltage_channel')" :value="previewSchema.door_open_voltage_field" />
                    </div>
                  </template>

                  <template v-else-if="sensorDetails.sensor_type === 'temperature'">
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <CardInfo :title="t('sensor.fields.critical_temperature')" :value="previewSchema.temperature" />
                    </div>
                  </template>
                </div>

                <div v-if="selectedSchema" class="border-t border-gray-200 pt-4">
                  <FilledButton
                    :label="t('sensor.detail.schema.save')"
                    class="w-full md:w-auto justify-center !bg-blue-600 hover:!bg-blue-700"
                    @click="saveSchema"
                  />
                </div>
              </div>
            </div>

            <!-- Aba Configurações -->
            <div v-show="activeTab === 'settings'">
              <div class="space-y-4">
                <h2 class="text-lg font-bold text-gray-900">{{ t('alarms.name') }}</h2>

                <template v-if="sensorDetails.sensor_type === SensorType.ELECTRICITY">
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('No data')"
                    :label="t('alarms.type.no_data')"
                    :isActive="hasNoDataAlarm"
                    @toggle="(profileId) => store.toggleNoDataAlarm(sensorDetails.id, sensorDetails.sensor_type, profileId)"
                    :profileId="sensorDetails.alarm_no_data_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Interruption')"
                    :label="t('alarms.type.interruption')"
                    :isActive="hasInterruptionAlarm"
                    @toggle="(profileId) => store.toggleInterruptionAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_interrupt_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Low load')"
                    :label="t('alarms.type.low_load')"
                    :isActive="hasLowLoadAlarm"
                    @toggle="(profileId) => store.toggleLowLoadAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_low_load_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Activation peak hour')"
                    :label="t('alarms.type.peak_hour')"
                    :isActive="hasPeakHourAlarm"
                    @toggle="(profileId) => store.togglePeakHourAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_peak_hour_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Defrost time')"
                    :label="t('alarms.type.defrost_time')"
                    :isActive="hasDefrostTimeAlarm"
                    @toggle="(profileId) => store.toggleDefrostTimeAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_defrost_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Burnt resistor')"
                    :label="t('alarms.type.burnt_resistor')"
                    :isActive="hasBurntResistor"
                    @toggle="(profileId) => store.toggleBurntResistorAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_defrost_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Empty mixer')"
                    :label="t('alarms.type.empty_mixer')"
                    :isActive="hasEmptyMixerAlarm"
                    @toggle="(profileId) => store.toggleEmptyMixerAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_empty_mixer_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Outside regular time')"
                    :label="t('alarms.type.outside_regular_time')"
                    :isActive="hasOutsideRegularTimeAlarm"
                    @toggle="(profileId) => store.toggleIOutsideRegularTimeAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_outside_regular_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Power factor')"
                    :label="t('alarms.type.power_factor')"
                    :isActive="hasPowerFactorAlarm"
                    @toggle="(profileId) => store.togglePowerFactorAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_power_factor_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Overvoltage')"
                    :label="t('alarms.type.overvoltage')"
                    :isActive="hasOvervoltageAlarm"
                    @toggle="(profileId) => store.toggleOvervoltageAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_overvoltage_sensor_id"
                  />
                </template>

                <template v-if="sensorDetails.sensor_type === SensorType.DOOR_OPEN">
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('No data')"
                    :label="t('alarms.type.no_data')"
                    :isActive="hasNoDataAlarm"
                    @toggle="(profileId) => store.toggleNoDataAlarm(sensorDetails.id, sensorDetails.sensor_type, profileId)"
                    :profileId="sensorDetails.alarm_no_data_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Door open')"
                    :label="t('alarms.type.door_open')"
                    :isActive="hasDoorOpen"
                    @toggle="(profileId) => store.toggleDoorOpenAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_door_open_sensor_id"
                  />
                </template>

                <template v-if="sensorDetails.sensor_type === SensorType.TEMPERATURE">
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('No data')"
                    :label="t('alarms.type.no_data')"
                    :isActive="hasNoDataAlarm"
                    @toggle="(profileId) => store.toggleNoDataAlarm(sensorDetails.id, sensorDetails.sensor_type, profileId)"
                    :profileId="sensorDetails.alarm_no_data_sensor_id"
                  />
                  <AlarmsConfig
                    v-if="isAlarmTypeAvailable('Critical temperature')"
                    :label="t('alarms.type.critical_temperature')"
                    :isActive="hasTemperatureAlarm"
                    @toggle="(profileId) => store.toggleTemperatureAlarm(sensorDetails.id, profileId)"
                    :profileId="sensorDetails.alarm_critical_temperature_sensor_id"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal de edição -->
        <EditSensorModal
          v-if="sensorDetails"
          :isOpen="isEditModalOpen"
          :sensorName="sensorDetails.name"
          :sensorId="sensorDetails.id"
          @close="closeEditModal"
          @saved="onSensorSaved"
        />

        <!-- Modal de confirmação para DESATIVAR -->
        <ConfirmationModal
          :isOpen="isArchiveModalOpen"
          :cancelText="t('sensor.detail.archive.cancel')"
          :confirmationText="isArchiveBusy ? t('sensor.detail.archive.confirming') : t('sensor.detail.archive.confirm')"
          @cancel="closeArchiveModal"
          @add="confirmArchive">
          <template #content>
            <div class="space-y-4">
              <div class="flex items-start gap-3">
                <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <svg class="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h2 class="text-lg font-semibold text-gray-900">{{ t('sensor.detail.archive.title') }}</h2>
                  <p class="mt-1 text-sm text-gray-600">
                    {{ t('sensor.detail.archive.intro', { name: sensorDetails?.name }) }}
                  </p>
                </div>
              </div>

              <ul class="space-y-2 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <li class="flex items-start gap-2">
                  <span class="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold text-amber-800">!</span>
                  <span>{{ t('sensor.detail.archive.bullet_alarms') }}</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold text-amber-800">!</span>
                  <span>{{ t('sensor.detail.archive.bullet_analysis') }}</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold text-amber-800">!</span>
                  <span>{{ t('sensor.detail.archive.bullet_lists') }}</span>
                </li>
              </ul>

              <p class="text-xs text-gray-500">
                {{ t('sensor.detail.archive.footer') }}
              </p>
            </div>
          </template>
        </ConfirmationModal>

        <!-- Modal de confirmação para REATIVAR -->
        <ConfirmationModal
          :isOpen="isUnarchiveModalOpen"
          :cancelText="t('sensor.detail.archive.cancel')"
          :confirmationText="isArchiveBusy ? t('sensor.detail.unarchive.confirming') : t('sensor.detail.unarchive.confirm')"
          @cancel="closeUnarchiveModal"
          @add="confirmUnarchive">
          <template #content>
            <div class="space-y-4">
              <div class="flex items-start gap-3">
                <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                  <svg class="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h2 class="text-lg font-semibold text-gray-900">{{ t('sensor.detail.unarchive.title') }}</h2>
                  <p class="mt-1 text-sm text-gray-600">
                    {{ t('sensor.detail.unarchive.intro', { name: sensorDetails?.name }) }}
                  </p>
                </div>
              </div>

              <p class="rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                {{ t('sensor.detail.unarchive.attention') }}
              </p>
            </div>
          </template>
        </ConfirmationModal>
      </div>
    </template>

    <template v-else>
      <div class="mx-auto max-w-7xl px-4 py-6">
        <p class="text-center text-gray-500">{{ t('sensor.detail.not_found') }}</p>
      </div>
    </template>
  </DefaultLayout>
</template>
